import { Children, FC, ReactNode, isValidElement } from 'react'
import { Box } from '@mui/material'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Figure } from './Figure'
import { Callout } from './Callout'
import { headingId, headingTitle } from '../core'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { FONT_WEIGHT, typeScale } from '@/shared/styles/typography'

type MarkdownBodyProps = {
  children: string
}

const toText = (node: ReactNode): string =>
  Children.toArray(node)
    .map((child) => {
      if (typeof child === 'string' || typeof child === 'number') {
        return String(child)
      }
      if (isValidElement<{ children?: ReactNode }>(child)) {
        return toText(child.props.children)
      }
      return ''
    })
    .join('')

/**
 * 手順の見出し。目次から飛べるよう、見出しの文言から起こした id を振る。
 * 目次側 (core.ts の extractSteps) と同じ headingId を使うので、
 * md の見出しを直せば両方が一度に追従する。
 *
 * `{#id}` の明示指定は表示から落とす。id を持てない見出しは属性ごと落とす。
 */
const StepHeading: FC<{ children?: ReactNode }> = ({ children }) => {
  const raw = toText(children)
  const id = headingId(raw)
  return <h3 id={id || undefined}>{headingTitle(raw)}</h3>
}

/**
 * ガイド本文（content/*.md）のレンダラ。
 *
 * 文章は md 側、体裁はここが持つ。md に書けるのは見出し (###)・箇条書き・
 * 番号付き・画像・引用 (= 警告)・表 のみ。
 *
 * 見出しは md の ### (手順の見出し) だけで、型は h4 (16/600)。
 * 区分見出し h2 (24) はページ側 (index.tsx) が持つ。
 * 罫線では区切らず、見出しの上の余白で区切る。手順の上 32px は
 * 区分の上 64px の半分で、階層を余白でも示している。
 */
export const MarkdownBody: FC<MarkdownBodyProps> = ({ children }) => (
  <Box
    sx={{
      color: colorTokens.fg.default,
      '& h3': {
        ...typeScale.h4,
        mt: 4,
        mb: 1,
        scrollMarginTop: '80px',
      },
      '& p': { ...typeScale.body, my: 1 },
      '& ul, & ol': { ...typeScale.body, my: 1, pl: 3 },
      '& li': { my: 0.5 },
      '& li > ul, & li > ol': { my: 0.5 },
      '& strong': { fontWeight: FONT_WEIGHT.semibold },
      '& a': { color: colorTokens.accent.primary, textDecoration: 'none' },
      // 表は罫線で区切らず、ヘッダ行の地色と行間で読ませる
      '& table': {
        ...typeScale.bodySm,
        my: 2,
        borderCollapse: 'collapse',
        width: '100%',
      },
      '& th': {
        textAlign: 'left',
        fontWeight: FONT_WEIGHT.semibold,
        backgroundColor: colorTokens.bg.subtle,
        px: 1.5,
        py: 1,
        verticalAlign: 'top',
      },
      '& td': {
        px: 1.5,
        py: 1,
        verticalAlign: 'top',
        borderTop: `1px solid ${colorTokens.border.faint}`,
      },
    }}
  >
    <Markdown
      remarkPlugins={[remarkGfm]}
      components={{
        h3: ({ children }) => <StepHeading>{children}</StepHeading>,
        img: ({ src, alt }) => <Figure src={src} alt={alt} />,
        blockquote: ({ children }) => <Callout>{children}</Callout>,
      }}
    >
      {children}
    </Markdown>
  </Box>
)
